/**
 * 마스터 패스워드 기반 암호화 서비스
 * PBKDF2 + AES-256-GCM을 사용한 독립적인 암호화
 */

/**
 * 마스터 패스워드로부터 암호화 키 생성
 * @param {string} password - 마스터 패스워드
 * @param {Uint8Array} salt - 솔트 (32바이트)
 * @returns {Promise<CryptoKey>} 암호화 키
 */
async function deriveKey(password, salt) {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    {
      name: 'AES-GCM',
      length: 256
    },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * 마스터 패스워드로 데이터 암호화
 * @param {string} data - 암호화할 데이터 (JSON 문자열)
 * @param {string} masterPassword - 마스터 패스워드
 * @returns {Promise<Object>} 암호화된 데이터 객체
 */
export async function encryptWithMasterPassword(data, masterPassword) {
  try {
    // 랜덤 솔트와 IV 생성
    const salt = crypto.getRandomValues(new Uint8Array(32));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    
    // 키 생성
    const key = await deriveKey(masterPassword, salt);
    
    // 데이터 암호화
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      dataBuffer
    );
    
    // Base64로 인코딩하여 반환
    return {
      encrypted: true,
      algorithm: 'AES-256-GCM',
      keyDerivation: 'PBKDF2',
      iterations: 100000,
      salt: Array.from(salt),
      iv: Array.from(iv),
      data: Array.from(new Uint8Array(encryptedBuffer))
    };
  } catch (error) {
    console.error('마스터 패스워드 암호화 실패:', error);
    throw new Error('데이터 암호화에 실패했습니다.');
  }
}

/**
 * 마스터 패스워드로 데이터 복호화
 * @param {Object} encryptedData - 암호화된 데이터 객체
 * @param {string} masterPassword - 마스터 패스워드
 * @returns {Promise<string>} 복호화된 데이터 (JSON 문자열)
 */
export async function decryptWithMasterPassword(encryptedData, masterPassword) {
  try {
    const { salt, iv, data } = encryptedData;
    
    // 배열을 Uint8Array로 변환
    const saltArray = new Uint8Array(salt);
    const ivArray = new Uint8Array(iv);
    const dataArray = new Uint8Array(data);
    
    // 키 생성
    const key = await deriveKey(masterPassword, saltArray);
    
    // 데이터 복호화
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: ivArray
      },
      key,
      dataArray
    );
    
    // 문자열로 디코딩
    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  } catch (error) {
    console.error('마스터 패스워드 복호화 실패:', error);
    throw new Error('패스워드가 올바르지 않거나 데이터가 손상되었습니다.');
  }
}

/**
 * 패스워드 강도 검증
 * @param {string} password - 검증할 패스워드
 * @returns {Object} 강도 정보
 */
export function validateMasterPassword(password) {
  if (!password || typeof password !== 'string') {
    return { valid: false, message: '패스워드를 입력해주세요.' };
  }
  
  if (password.length < 8) {
    return { valid: false, message: '패스워드는 최소 8자 이상이어야 합니다.' };
  }
  
  if (password.length < 12) {
    return { valid: true, message: '보통 강도입니다. 12자 이상 권장합니다.', strength: 'medium' };
  }
  
  if (password.length >= 16) {
    return { valid: true, message: '강력한 패스워드입니다.', strength: 'strong' };
  }
  
  return { valid: true, message: '적절한 강도입니다.', strength: 'good' };
}